/**
 * usePaginationPageSize — хук для сохранения выбранного пользователем pageSize.
 *
 * Проблема: при ре-рендере HotTable → componentDidUpdate → updateSettings(),
 * если pagination prop — новый объект (inline литерал), плагин сбрасывает
 * pageSize к начальному значению из пропсов.
 *
 * Решение: запоминаем pageSize при смене пользователем (beforePageSizeChange)
 * и подставляем запомненное значение в pagination config.
 */

import { useCallback, useMemo, useRef } from 'react';
import type Handsontable from 'handsontable';

type BeforePageSizeChange = NonNullable<Handsontable.GridSettings['beforePageSizeChange']>;

interface UsePaginationPageSizeOptions {
  pagination: Handsontable.GridSettings['pagination'];
  tableId?: string;
  /** Внешний beforePageSizeChange callback — вызывается после внутренней логики */
  beforePageSizeChange?: BeforePageSizeChange;
}

interface UsePaginationPageSizeResult {
  /** Pagination config с подставленным запомненным pageSize */
  resolvedPagination: Handsontable.GridSettings['pagination'];
  /** Callback для HotTable beforePageSizeChange */
  handleBeforePageSizeChange: BeforePageSizeChange;
}

export function usePaginationPageSize({
  pagination,
  tableId,
  beforePageSizeChange,
}: UsePaginationPageSizeOptions): UsePaginationPageSizeResult {
  const pageSizeRef = useRef<number | 'auto' | undefined>(undefined);
  const tableIdRef = useRef<string | undefined>(tableId);

  // При смене tableId — сбрасываем запомненный pageSize (другая таблица).
  if (tableIdRef.current !== tableId) {
    tableIdRef.current = tableId;
    pageSizeRef.current = undefined;
  }

  // Инициализация из пропсов (один раз).
  if (
    pageSizeRef.current === undefined &&
    pagination &&
    typeof pagination === 'object' &&
    'pageSize' in pagination
  ) {
    const initial = (pagination as { pageSize?: number | 'auto' }).pageSize;
    if (initial === 'auto' || typeof initial === 'number') {
      pageSizeRef.current = initial;
    }
  }

  const handleBeforePageSizeChange = useCallback<BeforePageSizeChange>(
    (...args) => {
      const nextPageSize = (args as unknown[])[1];
      if (nextPageSize === 'auto' || typeof nextPageSize === 'number') {
        pageSizeRef.current = nextPageSize;
      }
      return beforePageSizeChange?.(...args);
    },
    [beforePageSizeChange],
  );

  const resolvedPagination = useMemo<Handsontable.GridSettings['pagination']>(() => {
    if (pagination == null || pagination === true || pagination === false) {
      return pagination;
    }

    if (typeof pagination === 'object') {
      const propPageSize = 'pageSize' in pagination
        ? (pagination as { pageSize?: number | 'auto' }).pageSize
        : undefined;
      const remembered = pageSizeRef.current;
      const pageSize = remembered ?? propPageSize;

      // Фиксируем начальное значение, если ref ещё не инициализирован.
      if (remembered === undefined && (propPageSize === 'auto' || typeof propPageSize === 'number')) {
        pageSizeRef.current = propPageSize;
      }

      return {
        ...(pagination as Record<string, unknown>),
        pageSize,
      } as Handsontable.GridSettings['pagination'];
    }

    return pagination;
  }, [pagination]);

  return { resolvedPagination, handleBeforePageSizeChange };
}
